"use client";

import { type RefObject, useLayoutEffect, useRef, useState } from "react";
import type { PipDimensions } from "./pip-snap";

/** Ignore pre-layout 0×0 (or 1×1 clamp) reads so we do not lock a broken resize base. */
const MIN_LOCKABLE_EDGE_PX = 8;

interface UsePipMeasuredDimensionsOptions {
	/** Keep the first measured size as the resize base after it stabilises. */
	lockBase?: boolean;
	/**
	 * Re-run measurement when this changes (e.g. portal host attaching).
	 * Needed when the PiP node mounts after the first layout effect — refs alone
	 * do not retrigger the effect.
	 */
	observeKey?: unknown;
}

export interface UsePipMeasuredDimensionsResult {
	dimensions: PipDimensions;
	/** True once {@link lockBase} has captured the initial CSS-sized measurement. */
	isBaseLocked: boolean;
}

export function usePipMeasuredDimensions(
	pipRef: RefObject<HTMLElement | null>,
	fallbackDimensions: PipDimensions,
	options?: UsePipMeasuredDimensionsOptions,
): UsePipMeasuredDimensionsResult {
	const [dimensions, setDimensions] = useState(fallbackDimensions);
	const lockedBaseRef = useRef<PipDimensions | null>(null);
	const [isBaseLocked, setIsBaseLocked] = useState(false);
	const lockBase = options?.lockBase === true;
	const observeKey = options?.observeKey;

	useLayoutEffect(() => {
		if (lockBase && lockedBaseRef.current != null) {
			return;
		}

		const element = pipRef.current;
		if (!element || typeof ResizeObserver === "undefined") {
			return;
		}

		let observer: ResizeObserver | null = null;

		const measure = () => {
			if (lockBase && lockedBaseRef.current != null) {
				return;
			}

			const rect = element.getBoundingClientRect();
			const widthPx = Math.max(1, Math.round(rect.width));
			const heightPx = Math.max(1, Math.round(rect.height));
			const next = { widthPx, heightPx };
			const canLockBase =
				lockBase &&
				lockedBaseRef.current == null &&
				widthPx >= MIN_LOCKABLE_EDGE_PX &&
				heightPx >= MIN_LOCKABLE_EDGE_PX;

			if (canLockBase) {
				lockedBaseRef.current = next;
				setIsBaseLocked(true);
				observer?.disconnect();
				observer = null;
			}

			setDimensions((current) =>
				current.widthPx === widthPx && current.heightPx === heightPx
					? current
					: next,
			);
		};

		measure();
		observer = new ResizeObserver(measure);
		observer.observe(element);
		window.addEventListener("resize", measure);
		return () => {
			observer?.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [lockBase, observeKey, pipRef]);

	const resolvedDimensions =
		lockBase && lockedBaseRef.current != null
			? lockedBaseRef.current
			: dimensions;

	return {
		dimensions: resolvedDimensions,
		isBaseLocked,
	};
}
